import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex, useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import { useContext } from 'react';
import { Link, useLocation } from "react-router-dom";
import NotificationBell from 'components/notifications/NotificationBell';
import { SidebarContext } from 'utils/contexts/SidebarContext';
import { SIDEBAR_GUTTER, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from 'utils/constants';

export default function AdminNavbar(props: {
  secondary: boolean;
  brandText: string;
  fixed: boolean;
  onOpen: (...args: any[]) => any;
  breadcrumbParent?: { name: string; path: string } | null;
}) {

  const { secondary, brandText, breadcrumbParent } = props;
  const { isCollapsed } = useContext(SidebarContext);
  // Below 'md' the layout shows the hamburger drawer instead of the persistent sidebar, so the navbar
  // must not reserve any sidebar space there — it falls back to the original small-margin layout.
  const isPhone = useBreakpointValue({ base: true, md: false });
  // Mirrors the same reserved-space math the layout uses for the routed content box, so the navbar's
  // left edge always clears the sidebar's current width instead of the old fixed-260px/no-sidebar split.
  const sidebarOffset = (isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED) + SIDEBAR_GUTTER;

  // Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
  let secondaryText = useColorModeValue('gray.700', 'white');
  let navbarPosition = 'fixed' as const;
  let navbarFilter = 'none';
  let navbarBackdrop = 'blur(20px)';
  let navbarShadow = 'none';
  let navbarBg = useColorModeValue('rgba(244, 247, 254, 0.2)', 'rgba(11,20,55,0.5)');
  let navbarBorder = 'transparent';
  let secondaryMargin = '0px';
  let paddingX = '15px';
  let gap = '0px';


  const location = useLocation();
  // The breadcrumb's "Home" crumb must point at the current layout's own home, not always /admin/home
  // — otherwise an employee viewing any page would get a "Home" link that 404s/redirects them out of
  // their own section.
  const layoutBase = location.pathname.startsWith('/dipendente') ? '/dipendente' : '/admin';
  const isHome = location.pathname === `${layoutBase}/home`;

  return (
    <Box
      position={navbarPosition}
      boxShadow={navbarShadow}
      bg={navbarBg}
      borderColor={navbarBorder}
      filter={navbarFilter}
      backdropFilter={navbarBackdrop}
      backgroundPosition='center'
      backgroundSize='cover'
      borderRadius='16px'
      borderWidth='1.5px'
      borderStyle='solid'
      transitionDelay='0s, 0s, 0s, 0s, 0s'
      transitionDuration=' 0.25s, 0.25s, 0.25s, 0s, 0.2s'
      transitionProperty='box-shadow, background-color, filter, border, left'
      transitionTimingFunction='linear, linear, linear, linear, linear'
      alignItems={{ xl: 'center' }}
      display={secondary ? 'block' : 'flex'}
      minH='75px'
      justifyContent={{ xl: 'space-between' }} // Align children horizontally with space between
      lineHeight='25.6px'
      mx='auto'
      mt={secondaryMargin}
      pb='8px'
      left={isPhone ? undefined : `${sidebarOffset + 30}px`}
      right={{ base: '12px', md: '30px', lg: '30px', xl: '30px' }}
      px={{
        sm: paddingX,
        md: '10px'
      }}
      ps={{
        xl: '12px'
      }}
      pt='8px'
      top={{ base: '12px', md: '16px', xl: '18px' }}
      w={isPhone ? 'calc(100vw - 6%)' : undefined}
    >
      <Flex
        w="100%"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between" // Align children horizontally with space between
        ml="auto" // Push the Flex container to the right side
        mb={gap}
      >
        <Box minW={0}>
          <Breadcrumb separator={isHome ? null : "/"} mt={4}>
            <BreadcrumbItem color={secondaryText} fontSize='sm' mb='5px'>
              <BreadcrumbLink href={`#${breadcrumbParent ? breadcrumbParent.path : `${layoutBase}/home`}`} color={secondaryText} style={{ outline: 'none', boxShadow: 'none' }}>
                {breadcrumbParent ? breadcrumbParent.name : 'Home'}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {!isHome && (
              <BreadcrumbItem color={secondaryText} fontSize='sm'>
                <BreadcrumbLink as={Link} to={location.pathname} color={secondaryText} style={{ outline: 'none', boxShadow: 'none' }}>
                  {brandText}
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </Breadcrumb>
        </Box>
        <NotificationBell />
      </Flex>
    </Box>
  );
}
